import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';

interface Point {
  x: number;
  y: number;
}

// A circuit trace: a polyline on the board, with its length and the length at each point.
interface Trace {
  points: Point[];
  lengths: number[];
  length: number;
  /** The trace ends in a pin on one of the solids (it feeds the middle). */
  pinned: boolean;
}

// A current running along a trace: a jagged, glowing length of the trace that flickers as it goes.
interface Current {
  trace: Trace;
  pos: number;
  speed: number;
  span: number;
  hue: number;
}

// A lightning bolt jumping between two points of the board, its jags redrawn every few milliseconds.
interface Bolt {
  from: Point;
  to: Point;
  path: Point[];
  branches: Point[][];
  life: number;
  total: number;
  hue: number;
  sway: number;
  width: number;
  flicker: number;
}

// A spark thrown off where a current or a bolt lands: a short streak that flies, falls and dies.
interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  total: number;
  hue: number;
}

type RGB = [ number, number, number ];

// how strongly the electricity (currents, bolts, sparks) shows: softened twice by 15% (owner,
// 2026-10-10), and a further fifth when the board is awake, where it got loud
const ELECTRIC = 0.72;
const WHITE: RGB = [ 255, 255, 255 ];

// A touch screen has no pointer to hover over the board, so a phone's board only woke while the
// counter counted and the electricity hardly showed there (owner, 2026-10-10: "mobilde
// elektrikler görünmüyor"). There it stays half awake on its own and surges every few seconds,
// as a desktop board does under a passing pointer: how awake it idles, how awake a surge gets,
// and the seconds between two surges (the shortest wait, and how much longer it may be)
const TOUCH_IDLE = 0.36;
const TOUCH_SURGE = 0.82;
const SURGE_EVERY = 4.5;
const SURGE_SPREAD = 3.5;
// a finger on the stage wakes the board fully, and it stays awake this long after the finger lifts
const TOUCH_LINGER = 1.4;
// the still board (reduced motion) is one moment of a board this awake, so it shows its electricity too
const STILL_STORM = 0.5;

/**
 * The circuit board behind the home hero: traces drawn across the stage, electric currents
 * crackling along them, lightning jumping between the nodes, sparks where a current lands. The hero's host calls `excite(true)` when the pointer
 * enters it and the board wakes up: more currents, faster and brighter, bolts leaping from the
 * pointer to the nodes around it. The colours are the theme's (--theme-color and
 * --theme-color-secondary), read live, so it follows the visitor's theme. The middle of the
 * board is cleared softly so the hero's text stays readable. On a touch screen, where nothing
 * hovers, the board keeps itself half awake and surges now and then (see `breathe()`). Reduced
 * motion draws one still moment of the board, its electricity included, once.
 */
@Component( {
  selector: 'app-hero-circuit',
  templateUrl: './hero-circuit.component.html',
  styleUrls: [ './hero-circuit.component.scss' ],
} )
export class HeroCircuitComponent implements AfterViewInit, OnDestroy {

  @ViewChild( 'canvas', { static: true } ) canvasRef!: ElementRef<HTMLCanvasElement>;

  /** A still board: one moment of an awake board, drawn once and never animated (the page's wallpaper). */
  @Input() frozen = false;
  /** Fade the middle of the board out, for text that sits over it. */
  @Input() clearing = true;
  /** How awake the still moment is (0..1): the page's wallpaper takes a storm, a strip a calm. */
  @Input() storm = 0.9;
  /**
   * Elements over the board that are part of the circuit (the hero's logo, motto and counter):
   * traces run up to their edges and end in a pin there, bolts strike those pins, and nothing is
   * drawn under them.
   */
  @Input() solids: HTMLElement[] = [];
  /**
   * The element the pointer wakes the board through (the hero stage). The board listens there
   * itself, outside Angular, so a mousemove never runs change detection.
   */
  @Input() stage: HTMLElement | null = null;
  /** Extra room a run keeps from each solid, by index ({ x: sides, y: top and bottom }, px). */
  @Input() solidRoom: ( { x: number; y: number } | null )[] = [];

  private ctx: CanvasRenderingContext2D | null = null;
  private raf = 0;
  private last = 0;
  private width = 0;
  private height = 0;
  private dpr = 1;
  private traces: Trace[] = [];
  // the solids' boxes in board pixels, measured from the page, and when they were last measured
  // charge: the energy a solid has collected from the currents that reached it (0..1); it
  // crackles along its edge with it and lets it go slowly (owner, 2026-10-10: "enerji orada
  // toplanıyor"; the glow round the edge was dropped the same night)
  private blocks: { x: number; y: number; w: number; h: number; charge: number; rx: number; ry: number }[] = [];
  private blocksAge = 0;
  // a new layout on every page load (owner, 2026-10-10); the seed stays for this instance, so a
  // resize re-lays the same board at the new size instead of a stranger
  private readonly seed = Math.floor( Math.random() * 0x7fffffff );
  private nodes: Point[] = [];
  private currents: Current[] = [];
  private bolts: Bolt[] = [];
  private sparks: Spark[] = [];
  private currentDebt = 0;
  private boltDebt = 0;
  private energy = 0;
  private target = 0;
  // what wakes the board: the pointer over it, or a charge from outside (the counter counting)
  private hover = 0;
  private charged = 0;
  // a short wake from outside (the site's appearance changed): rises and settles on its own, eased in and out
  private pulsed = 0;
  private pulseAt = -1;
  private pulseFor = 3;
  // a touch screen (nothing hovers there): the board's own idle wakefulness and its surges, a
  // finger on the stage and how long it lingers after the finger lifts, and when the last touch
  // came (a tap is followed by mouse events the browser makes up; those are not a pointer)
  private touch = false;
  private idle = 0;
  private surgeAt = -1;
  private surgeIn = 6 + Math.random() * 2;
  private touching = false;
  private held = 0;
  private lastTouch = -1e9;
  private clientX = NaN;
  private clientY = NaN;
  private pointerX = -1e4;
  private pointerY = -1e4;
  // the theme's colours the electricity draws from: primary, secondary, tertiary, with near
  // duplicates dropped (a theme whose secondary is the primary's own blue gets a turned hue
  // instead), so every theme sparks in more than one colour (owner, 2026-10-10)
  private colors: RGB[] = [ [ 0, 186, 255 ], [ 0, 255, 255 ] ];
  // the surface under the board is light: the bolt's core goes darker than its colour, not white
  private lightSurface = true;
  private colorAge = 99;
  private visible = true;
  private still = false;
  private resizeObserver: ResizeObserver | null = null;
  private intersection: IntersectionObserver | null = null;
  private onVisibility = () => this.sync();
  private probe: HTMLSpanElement | null = null;
  // the appearance (dark / light, the product theme) is a class and style on the app root: the
  // board recolours the moment they change, instead of at its next periodic read
  private themeWatch: MutationObserver | null = null;
  private stageListeners: [ string, EventListener ][] = [];
  private scheme: MediaQueryList | null = null;
  private onScheme = () => this.recolor();

  constructor( private zone: NgZone, private host: ElementRef<HTMLElement> ) {
  }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext( '2d' );
    if ( !this.ctx ) { return; }
    this.still = this.frozen || ( typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches );
    this.touch = !this.still && !!window.matchMedia && window.matchMedia( '(hover: none)' ).matches;
    this.probe = document.createElement( 'span' );
    this.probe.style.display = 'none';
    this.host.nativeElement.appendChild( this.probe );

    this.zone.runOutsideAngular( () => {
      this.resizeObserver = new ResizeObserver( () => this.resize() );
      this.resizeObserver.observe( this.host.nativeElement );
      this.intersection = new IntersectionObserver( entries => {
        this.visible = entries.some( e => e.isIntersecting );
        this.sync();
      }, { threshold: 0.02 } );
      this.intersection.observe( this.host.nativeElement );
      document.addEventListener( 'visibilitychange', this.onVisibility );
      const root = document.getElementById( 'app-component' ) || document.body;
      this.themeWatch = new MutationObserver( () => this.recolor() );
      this.themeWatch.observe( root, { attributes: true, attributeFilter: [ 'class', 'style' ] } );
      this.scheme = window.matchMedia ? window.matchMedia( '(prefers-color-scheme: dark)' ) : null;
      this.scheme?.addEventListener( 'change', this.onScheme );
      if ( this.stage ) {
        // the mouse events a tap makes up are skipped: on a phone they used to leave the board
        // awake until the next tap somewhere else (a mouseenter with no mouseleave)
        this.stageListeners = [
          [ 'mouseenter', () => { if ( !this.fromTouch() ) { this.excite( true ); } } ],
          [ 'mouseleave', () => { if ( !this.fromTouch() ) { this.excite( false ); } } ],
          [ 'mousemove', ( ev: Event ) => {
            if ( !this.fromTouch() ) { this.pointer( ( ev as MouseEvent ).clientX, ( ev as MouseEvent ).clientY ); }
          } ],
          [ 'touchstart', ( ev: Event ) => this.touched( ev as TouchEvent ) ],
          [ 'touchmove', ( ev: Event ) => this.touched( ev as TouchEvent ) ],
          [ 'touchend', ( ev: Event ) => this.touched( ev as TouchEvent ) ],
          [ 'touchcancel', ( ev: Event ) => this.touched( ev as TouchEvent ) ],
        ];
        for ( const [ type, fn ] of this.stageListeners ) { this.stage.addEventListener( type, fn, { passive: true } ); }
      }
      this.resize();
      this.sync();
    } );
  }

  ngOnDestroy(): void {
    this.stop();
    for ( const [ type, fn ] of this.stageListeners ) { this.stage?.removeEventListener( type, fn ); }
    this.resizeObserver?.disconnect();
    this.intersection?.disconnect();
    document.removeEventListener( 'visibilitychange', this.onVisibility );
    this.themeWatch?.disconnect();
    this.scheme?.removeEventListener( 'change', this.onScheme );
    this.probe?.remove();
  }

  /** The appearance changed: take the new colours now, and redraw a still board. */
  private recolor(): void {
    this.readColors();
    if ( this.still ) { this.draw( 0 ); }
  }

  /** The pointer is over the hero (or a finger touched it): the board wakes up, or settles. */
  excite( on: boolean ): void {
    this.hover = on ? 1 : 0;
    if ( !on ) {
      this.clientX = NaN;
      this.clientY = NaN;
      this.pointerX = -1e4;
      this.pointerY = -1e4;
    }
    this.retarget();
  }

  /** Something else charges the board (the downloads counter while it counts): awake until released. */
  charge( on: boolean ): void {
    this.charged = on ? 1 : 0;
    this.retarget();
  }

  /** A short wake (owner 2026-10-12: the appearance changed): the board comes alive and settles again over
   *  `seconds`, eased in and out. */
  pulse( seconds = 3 ): void {
    this.pulseFor = seconds;
    this.pulseAt = 0;
    this.sync();
  }

  private retarget(): void {
    this.target = Math.max( this.hover, this.charged, this.idle, this.pulsed );
    this.sync();
  }

  /**
   * A finger on the stage: the board wakes under it as it does under a mouse (the traces near
   * the finger light up, the currents start round it), and stays awake a moment after the finger
   * lifts, so a tap shows more than a flicker.
   */
  private touched( ev: TouchEvent ): void {
    this.lastTouch = performance.now();
    const finger = ev.touches[ 0 ];
    if ( finger ) {
      this.touching = true;
      this.held = 0;
      this.pointer( finger.clientX, finger.clientY );
      if ( !this.hover ) { this.excite( true ); }
    } else if ( this.touching ) {
      this.touching = false;
      this.held = TOUCH_LINGER;
    }
  }

  /** A mouse event that came with a touch (the browser makes them up after a tap), not a pointer. */
  private fromTouch(): boolean {
    return performance.now() - this.lastTouch < 1000;
  }

  /**
   * The board's own wakefulness on a touch screen (see TOUCH_IDLE): half awake all the while,
   * with a surge every few seconds that comes in fast, holds and settles, the way the desktop
   * board wakes and settles under a passing pointer. Also lets a lifted finger's wake run out.
   */
  private breathe( dt: number ): void {
    if ( !this.touching && this.held > 0 ) {
      this.held -= dt;
      if ( this.held <= 0 ) {
        this.held = 0;
        this.excite( false );
      }
    }
    if ( !this.touch ) { return; }
    this.surgeIn -= dt;
    if ( this.surgeIn <= 0 && this.surgeAt < 0 ) {
      this.surgeAt = 0;
      this.surgeIn = SURGE_EVERY + Math.random() * SURGE_SPREAD;
    }
    let lift = 0;
    if ( this.surgeAt >= 0 ) {
      this.surgeAt += dt;
      const t = this.surgeAt;
      lift = t < 0.35 ? t / 0.35 : t < 1.5 ? 1 : Math.max( 0, 1 - ( t - 1.5 ) / 0.9 );
      if ( t >= 2.4 ) { this.surgeAt = -1; }
    }
    this.idle = TOUCH_IDLE + ( TOUCH_SURGE - TOUCH_IDLE ) * lift;
    this.target = Math.max( this.hover, this.charged, this.idle, this.pulsed );
  }

  /** The pulse's level this frame: sin² over its length, so it eases in from rest and out to rest. */
  private pulseStep( dt: number ): void {
    if ( this.pulseAt < 0 ) { return; }
    this.pulseAt += dt;
    const p = this.pulseAt / this.pulseFor;
    this.pulsed = p >= 1 ? 0 : Math.pow( Math.sin( Math.PI * p ), 2 );
    if ( p >= 1 ) { this.pulseAt = -1; }
    this.target = Math.max( this.hover, this.charged, this.idle, this.pulsed );
  }

  /** Where the pointer is, in viewport pixels (the board maps it onto itself each frame). */
  pointer( clientX: number, clientY: number ): void {
    this.clientX = clientX;
    this.clientY = clientY;
  }

  // ---- the board ----

  private resize(): void {
    const canvas = this.canvasRef.nativeElement;
    const rect = this.host.nativeElement.getBoundingClientRect();
    if ( rect.width < 10 || rect.height < 10 ) { return; }
    this.dpr = Math.min( 2, window.devicePixelRatio || 1 );
    this.width = Math.round( rect.width );
    this.height = Math.round( rect.height );
    canvas.width = Math.round( this.width * this.dpr );
    canvas.height = Math.round( this.height * this.dpr );
    canvas.style.width = this.width + 'px';
    canvas.style.height = this.height + 'px';
    this.measureSolids();
    this.build();
    this.currents = [];
    this.bolts = [];
    this.sparks = [];
    // a still board is one moment of an awake one: the wallpaper's storm, or a calmer moment for
    // the stage under reduced motion, which used to show bare traces and no electricity at all
    if ( this.still ) {
      this.warm( this.frozen ? this.storm : STILL_STORM );
      this.draw( 0 );
    }
  }

  /** One moment of an awake board for the still draw: run the board for a while, then stop. */
  private warm( storm: number ): void {
    this.target = storm;
    for ( let i = 0; i < 60; i++ ) { this.step( 0.03 ); }
    this.target = 0;
    this.energy = storm;
  }

  /** The solids' boxes, in the board's own pixels; true when they moved since the last measure. */
  private measureSolids(): boolean {
    this.blocksAge = 0;
    const host = this.host.nativeElement.getBoundingClientRect();
    const sx = this.width / Math.max( 1, host.width );
    const sy = this.height / Math.max( 1, host.height );
    const next = ( this.solids || [] ).map( ( el, i ) => {
      const r = el.getBoundingClientRect();
      const room = this.solidRoom?.[ i ];
      return {
        x: ( r.left - host.left ) * sx, y: ( r.top - host.top ) * sy, w: r.width * sx, h: r.height * sy,
        charge: this.blocks[ i ]?.charge || 0, rx: room?.x || 0, ry: room?.y || 0,
      };
    } ).filter( b => b.w > 4 && b.h > 4 );
    const moved = next.length !== this.blocks.length
      || next.some( ( b, i ) => Math.abs( b.x - this.blocks[ i ].x ) > 2 || Math.abs( b.y - this.blocks[ i ].y ) > 2
        || Math.abs( b.w - this.blocks[ i ].w ) > 2 || Math.abs( b.h - this.blocks[ i ].h ) > 2 );
    this.blocks = next;
    return moved;
  }

  /** Inside one of the solids, grown by `margin` pixels on every side. */
  private inSolid( x: number, y: number, margin: number ): boolean {
    return this.solidAt( x, y, margin ) !== null;
  }

  private solidAt( x: number, y: number, margin: number ): { x: number; y: number; w: number; h: number; charge: number; rx: number; ry: number } | null {
    for ( const b of this.blocks ) {
      // the margin for a run (the pin distance) takes the solid's own extra room; the clearance
      // under the solid (a smaller margin) does not
      const mx = margin + ( margin >= this.PIN ? b.rx : 0 );
      const my = margin + ( margin >= this.PIN ? b.ry : 0 );
      if ( x >= b.x - mx && x <= b.x + b.w + mx && y >= b.y - my && y <= b.y + b.h + my ) { return b; }
    }
    return null;
  }

  // a trace stops this far from a solid's edge (its pin), and the solid is kept clear this far
  private readonly PIN = 16;
  private readonly CLEAR = 6;

  /**
   * The last point of a run before it enters a solid: the run from `a` toward `b` is walked in
   * small steps and stops where the next step would be inside the solid's margin.
   */
  private clipToSolid( a: Point, b: Point ): Point | null {
    const dist = Math.hypot( b.x - a.x, b.y - a.y );
    const steps = Math.max( 1, Math.ceil( dist / 2 ) );
    let last: Point | null = null;
    for ( let i = 1; i <= steps; i++ ) {
      const p = { x: a.x + ( b.x - a.x ) * i / steps, y: a.y + ( b.y - a.y ) * i / steps };
      if ( this.inSolid( p.x, p.y, this.PIN ) ) { return last; }
      last = p;
    }
    return b;
  }

  /**
   * The last points of a run that met a solid at `end` on its way to `target`: a diagonal run
   * keeps its pin; a straight one is cut `back` pixels short and turned 45 degrees toward the
   * solid's middle, ending in a pin where that diagonal meets the solid.
   */
  private approach( prev: Point, end: Point, target: Point, back: number ): Point[] {
    const dx = target.x - prev.x;
    const dy = target.y - prev.y;
    if ( dx !== 0 && dy !== 0 ) { return [ end ]; }
    const len = Math.hypot( end.x - prev.x, end.y - prev.y );
    back = Math.min( back, len - 4 );
    if ( back < 6 ) { return [ end ]; }
    const ux = Math.sign( dx );
    const uy = Math.sign( dy );
    const cut = { x: end.x - ux * back, y: end.y - uy * back };
    const probe = { x: end.x + ux * ( this.PIN + 3 ), y: end.y + uy * ( this.PIN + 3 ) };
    const block = this.solidAt( probe.x, probe.y, this.PIN + 3 );
    if ( !block ) { return [ end ]; }
    const cx = block.x + block.w / 2;
    const cy = block.y + block.h / 2;
    const side = ux !== 0 ? ( Math.sign( cy - cut.y ) || 1 ) : ( Math.sign( cx - cut.x ) || 1 );
    const dir = ux !== 0 ? { x: ux, y: side } : { x: side, y: uy };
    const far = { x: cut.x + dir.x * 600, y: cut.y + dir.y * 600 };
    const pin = this.clipToSolid( cut, far );
    if ( !pin || pin === far || Math.hypot( pin.x - cut.x, pin.y - cut.y ) < 4 ) { return [ cut ]; }
    return [ cut, pin ];
  }

  /** Lay the traces: circuit runs with 45 degree jogs, from the sides and the top, ending in nodes. */
  private build(): void {
    // a finer pitch on a phone's narrow board, so the runs round the logo, motto and counter
    // there are as many and as close as the desktop's round its wider middle
    const unit = this.width > 900 ? 26 : this.width > 560 ? 22 : 18;
    const cols = Math.floor( this.width / unit );
    const rows = Math.floor( this.height / unit );
    // by area, with a floor by width so a low strip still carries a few runs
    const count = Math.max( Math.round( ( cols * rows ) / 34 ), Math.round( cols / 5 ) );
    const rand = this.seeded( this.seed ^ ( cols * 7919 + rows * 104729 ) );
    const traces: Trace[] = [];
    const toPx = ( p: Point ): Point => ( { x: p.x * unit + unit / 2, y: p.y * unit + unit / 2 } );
    // a board taller than wide (a phone) runs up and down more often: across it a run soon meets
    // the solids or the edge, along it a run fills the sides beside the solids
    const verticalShare = this.height > this.width * 1.1 ? 0.5 : 0.28;
    // a start inside a solid is thrown away and another drawn, up to three tries per trace, so a
    // board mostly covered by its solids (a phone) still carries its share of runs
    for ( let i = 0; i < count * 3 && traces.length < count; i++ ) {
      const vertical = rand() < verticalShare;
      let cx = Math.floor( rand() * cols );
      let cy = Math.floor( rand() * rows );
      // never start inside a solid
      if ( this.inSolid( toPx( { x: cx, y: cy } ).x, toPx( { x: cx, y: cy } ).y, this.PIN ) ) { continue; }
      const dir = rand() < 0.5 ? 1 : -1;
      const points = [ { x: cx, y: cy } ];
      const segments = 3 + Math.floor( rand() * 4 );
      for ( let s = 0; s < segments; s++ ) {
        const run = 2 + Math.floor( rand() * 7 );
        if ( vertical ) { cy += dir * run; } else { cx += dir * run; }
        points.push( { x: cx, y: cy } );
        // a jog: one or two cells at 45 degrees, then on
        if ( rand() < 0.65 ) {
          const side = rand() < 0.5 ? 1 : -1;
          const jog = 1 + Math.floor( rand() * 2 );
          if ( vertical ) { cx += side * jog; cy += dir * jog; } else { cy += side * jog; cx += dir * jog; }
          points.push( { x: cx, y: cy } );
        }
        if ( cx < 0 || cy < 0 || cx > cols || cy > rows ) { break; }
      }
      // in pixels, and cut where a run meets a solid: the run ends in a pin at its edge, and it
      // always comes in at 45 degrees, never straight on (owner, 2026-10-10)
      const px: Point[] = [ toPx( points[ 0 ] ) ];
      for ( let k = 1; k < points.length; k++ ) {
        const prev = px[ px.length - 1 ];
        const target = toPx( points[ k ] );
        const end = this.clipToSolid( prev, target );
        if ( end === null ) { break; }
        if ( end !== target ) {
          px.push( ...this.approach( prev, end, target, unit * ( 1 + rand() ) ) );
          break;
        }
        px.push( end );
      }
      const t = this.trace( px );
      if ( t.length > unit * 3 ) { traces.push( t ); }
    }
    // feeders: runs from the board's edges to the solids, so there are always lines carrying
    // current into the middle (owner, 2026-10-10); they wander like the other traces, short runs
    // with 45 degree jogs, never one long straight line (owner: "dümdüz bir sürü çizgi")
    if ( this.blocks.length ) {
      // a wide board gets fewer feeders from the sides (they bunched up there) and more from the
      // top and bottom; a side feeder may set off part of the way in, not always at the far edge
      const wide = this.width / Math.max( 1, this.height );
      const feeders = Math.max( 3, Math.round( count / ( wide > 2.4 ? 11 : wide > 1.6 ? 8 : 5 ) ) );
      const sideShare = wide > 1.6 ? 0.3 : 0.5;
      // the feeders aim anywhere along the whole cluster of solids (its full height from the side,
      // its full width from above and below), not at one solid's middle, so they spread out
      const span = {
        x0: Math.min( ...this.blocks.map( b => b.x ) ), x1: Math.max( ...this.blocks.map( b => b.x + b.w ) ),
        y0: Math.min( ...this.blocks.map( b => b.y ) ), y1: Math.max( ...this.blocks.map( b => b.y + b.h ) ),
      };
      const block = { x: span.x0, y: span.y0, w: span.x1 - span.x0, h: span.y1 - span.y0 };
      for ( let i = 0; i < feeders * 3 && traces.filter( t => t.pinned ).length < feeders + 3; i++ ) {
        const fromSide = rand() < sideShare;
        const points: Point[] = [];
        // the sides alternate and the aim walks through four bands of the span, so the feeders
        // share the cluster evenly instead of bunching on one edge (owner, 2026-10-10)
        const slot = ( i % 4 ) / 4 + rand() * 0.25;
        if ( fromSide ) {
          // from anywhere on the side; the sideways drift toward the aim is spread evenly over the
          // whole way in (a shallow staircase that follows the straight line from start to aim),
          // so the wider the board, the slower the feeders close in and nothing bunches up
          // (owner, 2026-10-10)
          const left = i % 2 === 0;
          const dir = left ? 1 : -1;
          const aimY = block.y + block.h * ( 0.05 + slot * 0.9 );
          const way = rand() < 0.5 ? 0 : rand() * 0.35;
          const x0 = left ? unit / 2 + way * block.x : this.width - unit / 2 - way * ( this.width - block.x - block.w );
          const y0 = unit + rand() * ( this.height - 2 * unit );
          let x = x0;
          let y = y0;
          points.push( { x, y } );
          const goal = left ? block.x : block.x + block.w;
          for ( let s = 0; s < 60 && ( left ? x < goal + unit : x > goal - unit ); s++ ) {
            x += dir * unit * ( 2 + Math.floor( rand() * 4 ) );
            points.push( { x, y } );
            const frac = Math.min( 1, ( x - x0 ) / ( goal - x0 || 1 ) );
            const off = y0 + ( aimY - y0 ) * frac - y;
            if ( Math.abs( off ) >= unit * 0.9 ) {
              const jog = unit * Math.min( 2, Math.max( 1, Math.round( Math.abs( off ) / unit ) ) );
              x += dir * jog;
              y += Math.sign( off ) * jog;
              points.push( { x, y } );
            } else if ( rand() < 0.2 ) {
              x += dir * unit;
              y += ( rand() < 0.5 ? 1 : -1 ) * unit;
              points.push( { x, y } );
            }
          }
          points.push( { x: block.x + block.w / 2, y } );
        } else {
          const top = i % 2 === 0;
          const dir = top ? 1 : -1;
          const aimX = block.x + block.w * ( 0.05 + slot * 0.9 );
          const y0 = top ? unit / 2 : this.height - unit / 2;
          const x0 = unit + rand() * ( this.width - 2 * unit );
          let y = y0;
          let x = x0;
          points.push( { x, y } );
          const goal = top ? block.y : block.y + block.h;
          for ( let s = 0; s < 60 && ( top ? y < goal + unit : y > goal - unit ); s++ ) {
            y += dir * unit * ( 1 + Math.floor( rand() * 3 ) );
            points.push( { x, y } );
            const frac = Math.min( 1, ( y - y0 ) / ( goal - y0 || 1 ) );
            const off = x0 + ( aimX - x0 ) * frac - x;
            if ( Math.abs( off ) >= unit * 0.9 ) {
              const jog = unit * Math.min( 2, Math.max( 1, Math.round( Math.abs( off ) / unit ) ) );
              y += dir * jog;
              x += Math.sign( off ) * jog;
              points.push( { x, y } );
            } else if ( rand() < 0.2 ) {
              y += dir * unit;
              x += ( rand() < 0.5 ? 1 : -1 ) * unit;
              points.push( { x, y } );
            }
          }
          points.push( { x, y: block.y + block.h / 2 } );
        }
        if ( this.inSolid( points[ 0 ].x, points[ 0 ].y, this.PIN ) ) { continue; }
        const px: Point[] = [ points[ 0 ] ];
        for ( let k = 1; k < points.length; k++ ) {
          const prev = px[ px.length - 1 ];
          const target = points[ k ];
          const end = this.clipToSolid( prev, target );
          if ( end === null ) { break; }
          if ( end !== target ) {
            px.push( ...this.approach( prev, end, target, unit * ( 1 + rand() ) ) );
            break;
          }
          px.push( end );
        }
        const t = this.trace( px );
        if ( t.pinned && t.length > unit * 2 ) { traces.push( t ); }
      }
    }
    this.traces = traces;
    this.nodes = [];
    for ( const t of traces ) {
      this.nodes.push( t.points[ 0 ], t.points[ t.points.length - 1 ] );
    }
  }

  /** A trace from its points: lengths along it, and whether it ends in a pin on a solid. */
  private trace( px: Point[] ): Trace {
    const lengths = [ 0 ];
    for ( let k = 1; k < px.length; k++ ) {
      lengths.push( lengths[ k - 1 ] + Math.hypot( px[ k ].x - px[ k - 1 ].x, px[ k ].y - px[ k - 1 ].y ) );
    }
    const end = px[ px.length - 1 ];
    return { points: px, lengths, length: lengths[ lengths.length - 1 ], pinned: this.inSolid( end.x, end.y, this.PIN + 3 ) };
  }

  /** A small deterministic generator: one seed, one board, however often it is re-laid at a size. */
  private seeded( seed: number ): () => number {
    let s = seed >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  // ---- the loop ----

  private sync(): void {
    const run = this.visible && document.visibilityState === 'visible' && !this.still;
    if ( run && !this.raf ) {
      this.last = performance.now();
      // outside Angular whoever asks (the host's charge() runs in the zone): a frame must never
      // cost a change detection pass, and a frame re-requests in the zone it runs in
      this.zone.runOutsideAngular( () => {
        this.raf = requestAnimationFrame( t => this.frame( t ) );
      } );
    } else if ( !run ) {
      this.stop();
    }
  }

  private stop(): void {
    if ( this.raf ) {
      cancelAnimationFrame( this.raf );
      this.raf = 0;
    }
  }

  private frame( now: number ): void {
    this.raf = 0;
    const dt = Math.min( 0.05, ( now - this.last ) / 1000 );
    this.last = now;
    this.blocksAge += dt;
    if ( this.solids?.length && this.blocksAge > 1 && this.measureSolids() ) {
      this.build();
      this.currents = [];
      this.bolts = [];
    }
    this.locatePointer();
    this.breathe( dt );
    this.pulseStep( dt );
    this.step( dt );
    this.draw( dt );
    this.raf = requestAnimationFrame( t => this.frame( t ) );
  }

  /** The pointer's viewport position mapped onto the board (the board is a little bigger than the stage). */
  private locatePointer(): void {
    if ( isNaN( this.clientX ) ) { return; }
    const rect = this.host.nativeElement.getBoundingClientRect();
    this.pointerX = ( this.clientX - rect.left ) * ( this.width / Math.max( 1, rect.width ) );
    this.pointerY = ( this.clientY - rect.top ) * ( this.height / Math.max( 1, rect.height ) );
  }

  private step( dt: number ): void {
    this.energy += ( this.target - this.energy ) * Math.min( 1, dt * 3.2 );
    if ( this.energy < 0.001 ) { this.energy = 0; }
    const e = this.energy;

    // currents: a quiet few at rest, a storm when awake
    const maxCurrents = 6 + Math.round( 50 * e );
    this.currentDebt += dt * ( 1.6 + 24 * e );
    while ( this.currentDebt >= 1 && this.currents.length < maxCurrents && this.traces.length ) {
      this.currentDebt -= 1;
      this.currents.push( {
        trace: this.pickTrace(),
        pos: 0,
        speed: ( 70 + 230 * e ) * ( 0.7 + Math.random() * 0.7 ),
        span: 26 + Math.random() * 18 + 30 * e,
        hue: this.pickHue(),
      } );
    }
    if ( this.currentDebt > 2 ) { this.currentDebt = 2; }

    for ( let i = this.currents.length - 1; i >= 0; i-- ) {
      const c = this.currents[ i ];
      c.pos += c.speed * dt;
      if ( c.pos >= c.trace.length ) {
        const end = c.trace.points[ c.trace.points.length - 1 ];
        this.burst( end, c.hue, 2 + Math.round( 7 * e ) );
        if ( Math.random() < 0.2 + 0.5 * e ) { this.leap( end, c.hue, 50 + 150 * e ); }
        if ( c.trace.pinned ) {
          const block = this.solidAt( end.x, end.y, this.PIN + 3 );
          if ( block ) { block.charge = Math.min( 1, block.charge + 0.16 ); }
        }
        this.currents.splice( i, 1 );
      }
    }

    // the solids let their charge go slowly, and a well charged one crackles along its edge
    for ( const b of this.blocks ) {
      if ( b.charge <= 0 ) { continue; }
      b.charge = Math.max( 0, b.charge - dt * ( e > 0.3 ? 0.25 : 0.5 ) );
      if ( b.charge > 0.4 && Math.random() < b.charge * 0.25 ) {
        const m = this.CLEAR;
        const onTop = Math.random() < 0.5;
        const at = Math.random() < 0.7
          ? { x: b.x - m + Math.random() * ( b.w + 2 * m ), y: onTop ? b.y - m : b.y + b.h + m }
          : { x: onTop ? b.x - m : b.x + b.w + m, y: b.y - m + Math.random() * ( b.h + 2 * m ) };
        this.burst( at, this.pickHue(), 1 + Math.round( 2 * b.charge ) );
      }
    }

    // bolts: a rare jump at rest, lightning across the board when awake (never from or round the
    // pointer; owner, 2026-10-10)
    this.boltDebt += dt * ( 0.35 + 7 * e );
    const maxBolts = 3 + Math.round( 12 * e );
    while ( this.boltDebt >= 1 && this.bolts.length < maxBolts && this.nodes.length ) {
      this.boltDebt -= 1;
      this.leap( this.pickNode( false ), this.pickHue(), 60 + 120 * e );
    }
    if ( this.boltDebt > 2 ) { this.boltDebt = 2; }

    for ( let i = this.bolts.length - 1; i >= 0; i-- ) {
      const b = this.bolts[ i ];
      b.life -= dt;
      b.flicker -= dt;
      if ( b.life <= 0 ) {
        this.bolts.splice( i, 1 );
        continue;
      }
      if ( b.flicker <= 0 ) {
        b.flicker = 0.035 + Math.random() * 0.045;
        b.path = this.jag( b.from, b.to, b.sway );
        b.branches = this.fork( b.path, b.sway );
      }
    }

    for ( let i = this.sparks.length - 1; i >= 0; i-- ) {
      const s = this.sparks[ i ];
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.vx *= 0.95;
      s.vy = s.vy * 0.95 + 140 * dt;
      s.life -= dt;
      if ( s.life <= 0 ) { this.sparks.splice( i, 1 ); }
    }

  }

  /** A colour for a new current or bolt: the primary half the time, the others share the rest. */
  private pickHue(): number {
    if ( this.colors.length < 2 || Math.random() < 0.5 ) { return 0; }
    return 1 + Math.floor( Math.random() * ( this.colors.length - 1 ) );
  }

  /** A trace for a new current: into the middle while awake, near the pointer, anywhere otherwise. */
  private pickTrace(): Trace {
    // awake (the counter counting, the pointer over the hero): electricity keeps flowing into the
    // solids, so most new currents take a pinned trace (owner, 2026-10-10)
    if ( this.energy > 0.1 && Math.random() < 0.25 + 0.5 * this.energy ) {
      const pinned = this.traces.filter( t => t.pinned );
      if ( pinned.length ) { return pinned[ Math.floor( Math.random() * pinned.length ) ]; }
    }
    if ( this.energy > 0.2 && this.pointerX > -1e3 && Math.random() < 0.7 ) {
      let best = this.traces[ 0 ];
      let bestD = Infinity;
      for ( let k = 0; k < 8; k++ ) {
        const t = this.traces[ Math.floor( Math.random() * this.traces.length ) ];
        const d = this.distance( t, this.pointerX, this.pointerY );
        if ( d < bestD ) { bestD = d; best = t; }
      }
      return best;
    }
    return this.traces[ Math.floor( Math.random() * this.traces.length ) ];
  }

  /** A node for a bolt to start from: one near the pointer when asked, any otherwise. */
  private pickNode( nearPointer: boolean ): Point {
    if ( !nearPointer ) { return this.nodes[ Math.floor( Math.random() * this.nodes.length ) ]; }
    let best = this.nodes[ 0 ];
    let bestD = Infinity;
    for ( let k = 0; k < 10; k++ ) {
      const n = this.nodes[ Math.floor( Math.random() * this.nodes.length ) ];
      const d = Math.hypot( n.x - this.pointerX, n.y - this.pointerY );
      if ( d < bestD ) { bestD = d; best = n; }
    }
    return best;
  }

  /** A bolt from a point to a node nearby (none when no node is within reach). */
  private leap( from: Point, hue: number, reach: number ): void {
    let to: Point | null = null;
    let bestD = Infinity;
    for ( let k = 0; k < 16; k++ ) {
      const n = this.nodes[ Math.floor( Math.random() * this.nodes.length ) ];
      const d = Math.hypot( n.x - from.x, n.y - from.y );
      if ( d > 24 && d < reach && d < bestD ) { bestD = d; to = n; }
    }
    if ( !to ) { return; }
    const e = this.energy;
    const sway = 2 + bestD * 0.14;
    this.bolts.push( {
      from, to, hue, sway,
      path: this.jag( from, to, sway ),
      branches: [],
      life: 0, total: 0, flicker: 0,
      width: 1.1 + 0.9 * e,
    } );
    const b = this.bolts[ this.bolts.length - 1 ];
    b.total = 0.12 + Math.random() * 0.18 + 0.12 * e;
    b.life = b.total;
    b.branches = this.fork( b.path, sway );
    this.burst( to, hue, 1 + Math.round( 3 * e ) );
  }

  /** A lightning path between two points: the midpoint pushed sideways, again and again. */
  private jag( a: Point, b: Point, sway: number ): Point[] {
    const dist = Math.hypot( b.x - a.x, b.y - a.y );
    const levels = Math.max( 2, Math.min( 5, Math.round( Math.log2( dist / 6 ) ) ) );
    let pts: Point[] = [ a, b ];
    let offset = sway;
    for ( let l = 0; l < levels; l++ ) {
      const next: Point[] = [ pts[ 0 ] ];
      for ( let i = 1; i < pts.length; i++ ) {
        const p = pts[ i - 1 ];
        const q = pts[ i ];
        const dx = q.x - p.x;
        const dy = q.y - p.y;
        const len = Math.hypot( dx, dy ) || 1;
        const push = ( Math.random() * 2 - 1 ) * offset;
        next.push( { x: ( p.x + q.x ) / 2 - dy / len * push, y: ( p.y + q.y ) / 2 + dx / len * push }, q );
      }
      pts = next;
      offset *= 0.55;
    }
    return pts;
  }

  /** Side branches of a bolt: one or two forks leaving the path at an angle, thinner and shorter. */
  private fork( path: Point[], sway: number ): Point[][] {
    const branches: Point[][] = [];
    const count = Math.random() < 0.55 ? 1 : ( Math.random() < 0.5 ? 2 : 0 );
    for ( let k = 0; k < count && path.length > 4; k++ ) {
      const i = 1 + Math.floor( Math.random() * ( path.length - 3 ) );
      const p = path[ i ];
      const dx = path[ i + 1 ].x - path[ i - 1 ].x;
      const dy = path[ i + 1 ].y - path[ i - 1 ].y;
      const base = Math.atan2( dy, dx );
      const angle = base + ( Math.random() < 0.5 ? 1 : -1 ) * ( 0.45 + Math.random() * 0.5 );
      const end = path[ path.length - 1 ];
      const len = Math.hypot( end.x - p.x, end.y - p.y ) * ( 0.3 + Math.random() * 0.35 ) + 6;
      branches.push( this.jag( p, { x: p.x + Math.cos( angle ) * len, y: p.y + Math.sin( angle ) * len }, sway * 0.6 ) );
    }
    return branches;
  }

  private burst( at: Point, hue: number, count: number ): void {
    for ( let i = 0; i < count; i++ ) {
      const a = Math.random() * Math.PI * 2;
      const v = 60 + Math.random() * 160;
      const total = 0.22 + Math.random() * 0.3;
      this.sparks.push( { x: at.x, y: at.y, vx: Math.cos( a ) * v, vy: Math.sin( a ) * v - 40, life: total, total, hue } );
    }
    if ( this.sparks.length > 300 ) { this.sparks.splice( 0, this.sparks.length - 300 ); }
  }

  /** The pointer's distance to a trace, by its points (close enough for the glow). */
  private distance( t: Trace, x: number, y: number ): number {
    let d = Infinity;
    for ( const p of t.points ) {
      const dd = Math.hypot( p.x - x, p.y - y );
      if ( dd < d ) { d = dd; }
    }
    return d;
  }

  // ---- the drawing ----

  private draw( dt: number ): void {
    const ctx = this.ctx;
    if ( !ctx ) { return; }
    this.colorAge += dt;
    if ( this.colorAge > 1.5 ) { this.readColors(); }
    const e = this.energy;
    const a = this.colors[ 0 ];
    ctx.setTransform( this.dpr, 0, 0, this.dpr, 0, 0 );
    ctx.clearRect( 0, 0, this.width, this.height );
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // the traces, brighter when awake, and lit near the pointer; a pad at both ends
    const base = 0.18 + 0.22 * e;
    for ( const t of this.traces ) {
      const near = this.pointerX > -1e3 ? Math.max( 0, 1 - this.distance( t, this.pointerX, this.pointerY ) / 220 ) : 0;
      const alpha = Math.min( 1, base + near * 0.4 * e );
      ctx.strokeStyle = this.rgba( a, alpha );
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo( t.points[ 0 ].x, t.points[ 0 ].y );
      for ( let k = 1; k < t.points.length; k++ ) { ctx.lineTo( t.points[ k ].x, t.points[ k ].y ); }
      ctx.stroke();
      const first = t.points[ 0 ];
      const end = t.points[ t.points.length - 1 ];
      ctx.strokeStyle = this.rgba( a, Math.min( 1, alpha + 0.25 ) );
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.arc( end.x, end.y, 3.2, 0, Math.PI * 2 );
      ctx.stroke();
      ctx.beginPath();
      ctx.arc( first.x, first.y, 2.2, 0, Math.PI * 2 );
      ctx.stroke();
    }

    // the solids are parts of the circuit: no trace runs under them. Only the board itself is
    // cleared here, before the electricity is drawn: a flash, a fork or a spark that lands on a
    // pin beside a solid spreads over its edge whole, where the clearing used to cut it off in a
    // straight line (owner, 2026-10-13). The content sits above the canvas, so the words stay on top.
    if ( this.blocks.length ) {
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000';
      for ( const b of this.blocks ) {
        const m = this.CLEAR;
        ctx.beginPath();
        this.roundRect( b.x - m, b.y - m, b.w + 2 * m, b.h + 2 * m, 10 );
        ctx.fill();
      }
      ctx.restore();
    }

    // the currents: a jagged, glowing length of the trace, new jags every frame
    const jitter = 1.2 + 2.2 * e;
    for ( const c of this.currents ) {
      const colour = this.colors[ c.hue ] || a;
      const from = Math.max( 0, c.pos - c.span );
      const to = Math.min( c.trace.length, c.pos );
      const n = Math.max( 4, Math.round( ( to - from ) / 5 ) );
      const pts: Point[] = [];
      let prev = this.pointAt( c.trace, from );
      pts.push( prev );
      for ( let i = 1; i <= n; i++ ) {
        const p = this.pointAt( c.trace, from + ( to - from ) * i / n );
        if ( i < n ) {
          const dx = p.x - prev.x;
          const dy = p.y - prev.y;
          const len = Math.hypot( dx, dy ) || 1;
          const push = ( Math.random() * 2 - 1 ) * jitter * ( 0.3 + 0.7 * i / n );
          pts.push( { x: p.x - dy / len * push, y: p.y + dx / len * push } );
        } else {
          pts.push( p );
        }
        prev = p;
      }
      const head = pts[ pts.length - 1 ];
      const tail = pts[ 0 ];
      this.glowPath( pts, colour, 1, 1.6 + 0.6 * e, [ tail, head ] );
      // forks at the head: short jags leaving the current sideways
      if ( Math.random() < 0.45 + 0.4 * e ) {
        const back = pts[ Math.max( 0, pts.length - 3 ) ];
        const dir = Math.atan2( head.y - back.y, head.x - back.x );
        const forks = Math.random() < 0.6 ? 1 : 2;
        for ( let f = 0; f < forks; f++ ) {
          const angle = dir + ( Math.random() < 0.5 ? 1 : -1 ) * ( 0.5 + Math.random() * 0.7 );
          const len = 5 + Math.random() * ( 9 + 10 * e );
          this.glowPath( this.jag( head, { x: head.x + Math.cos( angle ) * len, y: head.y + Math.sin( angle ) * len }, 2 ), colour, 0.8, 1 );
        }
      }
    }

    // the bolts: fast in, then fading, flickering all the while
    for ( const bolt of this.bolts ) {
      const colour = this.colors[ bolt.hue ] || a;
      const t = 1 - bolt.life / bolt.total;
      const envelope = t < 0.12 ? t / 0.12 : 1 - ( t - 0.12 ) / 0.88;
      const alpha = envelope * ( 0.7 + Math.random() * 0.3 );
      this.glowPath( bolt.path, colour, alpha, bolt.width );
      for ( const branch of bolt.branches ) {
        this.glowPath( branch, colour, alpha * 0.75, bolt.width * 0.6 );
      }
      // the flash where it lands
      const flash = ctx.createRadialGradient( bolt.to.x, bolt.to.y, 0, bolt.to.x, bolt.to.y, 10 + 6 * e );
      flash.addColorStop( 0, this.rgba( this.core( colour ), 0.8 * alpha * this.gain() ) );
      flash.addColorStop( 0.3, this.rgba( colour, 0.5 * alpha * this.gain() ) );
      flash.addColorStop( 1, this.rgba( colour, 0 ) );
      ctx.fillStyle = flash;
      ctx.beginPath();
      ctx.arc( bolt.to.x, bolt.to.y, 10 + 6 * e, 0, Math.PI * 2 );
      ctx.fill();
    }

    // the sparks: short streaks along their flight
    ctx.lineWidth = 1.2;
    for ( const s of this.sparks ) {
      const colour = this.colors[ s.hue ] || a;
      const k = s.life / s.total;
      ctx.strokeStyle = this.rgba( this.mix( colour, this.core( colour ), 0.5 * k ), Math.min( 1, k * 1.3 ) * this.gain() );
      ctx.beginPath();
      ctx.moveTo( s.x, s.y );
      ctx.lineTo( s.x - s.vx * 0.045, s.y - s.vy * 0.045 );
      ctx.stroke();
    }

    // the clearing: the middle of the board fades out softly, so the hero's text reads over it
    if ( !this.clearing ) { return; }
    // a little wider than the content, and deeper while the board storms, so the words and the
    // counter stay readable under the pointer (owner, 2026-10-10)
    const rx = Math.min( this.width * 0.5, 520 ) * ( 1 + 0.08 * e );
    const ry = Math.min( this.height * 0.42, 280 ) * ( 1 + 0.08 * e );
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.translate( this.width / 2, this.height / 2 );
    ctx.scale( rx, ry );
    const clearing = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
    clearing.addColorStop( 0, `rgba(0,0,0,${ 0.82 + 0.16 * e })` );
    clearing.addColorStop( 0.55, `rgba(0,0,0,${ 0.62 + 0.3 * e })` );
    clearing.addColorStop( 1, 'rgba(0,0,0,0)' );
    ctx.fillStyle = clearing;
    ctx.fillRect( -1, -1, 2, 2 );
    ctx.restore();
  }

  /**
   * A rounded rectangle on the current path; drawn by hand where the canvas has no roundRect
   * (Safari before 16), where the call threw and stopped the board after its first frame.
   */
  private roundRect( x: number, y: number, w: number, h: number, r: number ): void {
    const ctx = this.ctx!;
    if ( typeof ctx.roundRect === 'function' ) {
      ctx.roundRect( x, y, w, h, r );
      return;
    }
    r = Math.max( 0, Math.min( r, w / 2, h / 2 ) );
    ctx.moveTo( x + r, y );
    ctx.arcTo( x + w, y, x + w, y + h, r );
    ctx.arcTo( x + w, y + h, x, y + h, r );
    ctx.arcTo( x, y + h, x, y, r );
    ctx.arcTo( x, y, x + w, y, r );
    ctx.closePath();
  }

  /** How strongly the electricity shows now: softer still while the board is awake. */
  private gain(): number {
    // a still board is already faded by the page (its opacity), so it keeps its electricity whole
    return this.frozen ? 1 : ELECTRIC * ( 1 - 0.2 * this.energy );
  }

  /**
   * An electric stroke: a wide soft glow, a coloured body and a near-white core, all on one path.
   * With `fade` the alpha runs from nothing at the first point to full at the second (a current's tail).
   */
  private glowPath( pts: Point[], colour: RGB, alpha: number, width: number, fade?: [ Point, Point ] ): void {
    const ctx = this.ctx!;
    alpha *= this.gain();
    ctx.beginPath();
    ctx.moveTo( pts[ 0 ].x, pts[ 0 ].y );
    for ( let i = 1; i < pts.length; i++ ) { ctx.lineTo( pts[ i ].x, pts[ i ].y ); }
    const passes: [ RGB, number, number ][] = [
      [ colour, ( this.lightSurface ? 0.28 : 0.2 ) * alpha, width * 4 ],
      [ colour, 0.65 * alpha, width * 1.5 ],
      [ this.core( colour ), 0.95 * alpha, width * 0.55 ],
    ];
    for ( const [ c, a, w ] of passes ) {
      if ( fade ) {
        const grad = ctx.createLinearGradient( fade[ 0 ].x, fade[ 0 ].y, fade[ 1 ].x, fade[ 1 ].y );
        grad.addColorStop( 0, this.rgba( c, 0 ) );
        grad.addColorStop( 1, this.rgba( c, a ) );
        ctx.strokeStyle = grad;
      } else {
        ctx.strokeStyle = this.rgba( c, a );
      }
      ctx.lineWidth = w;
      ctx.stroke();
    }
  }

  private pointAt( t: Trace, pos: number ): Point {
    const pts = t.points;
    for ( let k = 1; k < pts.length; k++ ) {
      if ( pos <= t.lengths[ k ] ) {
        const seg = t.lengths[ k ] - t.lengths[ k - 1 ];
        const f = seg > 0 ? ( pos - t.lengths[ k - 1 ] ) / seg : 0;
        return { x: pts[ k - 1 ].x + ( pts[ k ].x - pts[ k - 1 ].x ) * f, y: pts[ k - 1 ].y + ( pts[ k ].y - pts[ k - 1 ].y ) * f };
      }
    }
    return pts[ pts.length - 1 ];
  }

  /** The theme's two colours, as the page resolves them now. */
  private readColors(): void {
    this.colorAge = 0;
    if ( !this.probe ) { return; }
    const style = getComputedStyle( this.host.nativeElement );
    const read = ( name: string, fallback: RGB ): RGB => {
      const value = style.getPropertyValue( name ).trim();
      if ( !value ) { return fallback; }
      this.probe!.style.color = value;
      const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/.exec( getComputedStyle( this.probe! ).color );
      return m ? [ +m[ 1 ], +m[ 2 ], +m[ 3 ] ] : fallback;
    };
    const primary = read( '--theme-color', this.colors[ 0 ] );
    const picked: RGB[] = [ primary ];
    for ( const name of [ '--theme-color-secondary', '--theme-color-tertiary' ] ) {
      const c = read( name, primary );
      if ( this.lively( c ) && picked.every( p => this.hueApart( p, c ) ) ) { picked.push( c ); }
    }
    if ( picked.length < 2 ) { picked.push( this.turn( primary, 48 ) ); }
    this.colors = picked;
    const surface = read( '--codigrate-input-bg', read( '--background-color', [ 255, 255, 255 ] ) );
    this.lightSurface = ( 0.2126 * surface[ 0 ] + 0.7152 * surface[ 1 ] + 0.0722 * surface[ 2 ] ) > 140;
  }

  /** The brightest line of a bolt: near white on a dark surface, deeper than its colour on a light one. */
  private core( colour: RGB ): RGB {
    return this.lightSurface ? this.mix( colour, [ 0, 0, 0 ], 0.3 ) : this.mix( colour, WHITE, 0.75 );
  }

  /** A colour electricity can wear: saturated, neither near black nor near white (a theme's black tertiary is skipped). */
  private lively( c: RGB ): boolean {
    const lum = this.lum( c );
    return this.hueSat( c )[ 1 ] >= 0.25 && lum > 40 && lum < 230;
  }

  /** Two colours read as different: their hues are 15 degrees apart. */
  private hueApart( c: RGB, d: RGB ): boolean {
    const diff = Math.abs( this.hueSat( c )[ 0 ] - this.hueSat( d )[ 0 ] );
    return Math.min( diff, 360 - diff ) >= 15;
  }

  private hueSat( c: RGB ): [ number, number ] {
    const r = c[ 0 ] / 255, g = c[ 1 ] / 255, b = c[ 2 ] / 255;
    const max = Math.max( r, g, b ), min = Math.min( r, g, b );
    const delta = max - min;
    if ( delta < 1e-6 ) { return [ 0, 0 ]; }
    let h = max === r ? ( ( g - b ) / delta ) % 6 : max === g ? ( b - r ) / delta + 2 : ( r - g ) / delta + 4;
    h = ( h * 60 + 360 ) % 360;
    const l = ( max + min ) / 2;
    const sat = delta / ( 1 - Math.abs( 2 * l - 1 ) );
    return [ h, sat ];
  }

  private lum( c: RGB ): number {
    return 0.2126 * c[ 0 ] + 0.7152 * c[ 1 ] + 0.0722 * c[ 2 ];
  }

  /** The colour with its hue turned by `deg` degrees (same saturation and lightness). */
  private turn( c: RGB, deg: number ): RGB {
    const r = c[ 0 ] / 255, g = c[ 1 ] / 255, b = c[ 2 ] / 255;
    const max = Math.max( r, g, b ), min = Math.min( r, g, b );
    const l = ( max + min ) / 2;
    const [ h0, sat ] = this.hueSat( c );
    const s = sat || 0.6;
    const h = ( h0 + deg + 360 ) % 360;
    const k = ( n: number ) => ( n + h / 30 ) % 12;
    const a = s * Math.min( l, 1 - l );
    const f = ( n: number ) => l - a * Math.max( -1, Math.min( k( n ) - 3, 9 - k( n ), 1 ) );
    return [ f( 0 ) * 255, f( 8 ) * 255, f( 4 ) * 255 ];
  }

  private mix( c: RGB, d: RGB, k: number ): RGB {
    return [ c[ 0 ] + ( d[ 0 ] - c[ 0 ] ) * k, c[ 1 ] + ( d[ 1 ] - c[ 1 ] ) * k, c[ 2 ] + ( d[ 2 ] - c[ 2 ] ) * k ];
  }

  private rgba( c: RGB, alpha: number ): string {
    return `rgba(${ Math.round( c[ 0 ] ) },${ Math.round( c[ 1 ] ) },${ Math.round( c[ 2 ] ) },${ alpha })`;
  }
}
